import React, { useId, useState } from "react"
import { Collapsible } from "@base-ui/react/collapsible"
import { cva } from "class-variance-authority"
import { Icon } from "../icon"
import { Tooltip } from "../tooltip"
import { cn } from "../utils"
import { VisuallyHidden } from "../visually-hidden"

// =============================================================================
// Styles
// =============================================================================
const circleStyles = cva("shrink-0 rounded-full", {
  variants: {
    size: {
      small: "size-5",
      large: "size-6"
    }
  },
  defaultVariants: {
    size: "small"
  }
})

const tooltipTriggerStyles = [
  "inline-flex items-center justify-center bg-blue-800 text-white",
  "outline-2 outline-offset-2 outline-transparent focus-visible:outline-gray-900"
]

const collapsibleStyles = cva(
  [
    "absolute inset-y-0 left-0 inline-flex items-center rounded-full bg-blue-800 text-white",
    "outline-2 outline-offset-2 outline-transparent has-[:focus-visible]:outline-gray-900"
  ],
  {
    variants: {
      size: {
        small: "text-xs font-semibold leading-5",
        large: "text-sm font-semibold leading-6"
      }
    },
    defaultVariants: {
      size: "small"
    }
  }
)

const collapsibleTriggerStyles = cva(
  "inline-flex shrink-0 items-center justify-center rounded-full outline-none",
  {
    variants: {
      size: {
        small: "p-[0.125rem]",
        large: "p-1"
      }
    },
    defaultVariants: {
      size: "small"
    }
  }
)

const panelStyles = cva(
  [
    "group/pro-badge overflow-hidden whitespace-nowrap",
    "w-[var(--collapsible-panel-width)] data-[starting-style]:w-0 data-[ending-style]:w-0",
    "transition-[width] duration-150 ease-out"
  ],
  {
    variants: {
      size: {
        small: "pr-1",
        large: "pr-[0.375rem]"
      }
    },
    defaultVariants: {
      size: "small"
    }
  }
)

const labelStyles = [
  "transition-opacity duration-150 ease-out",
  "group-data-[starting-style]/pro-badge:opacity-0",
  "group-data-[ending-style]/pro-badge:opacity-0"
]

// =============================================================================
// Props
// =============================================================================
/**
 * The mark shown in the badge circle. `diamond` is the paid/pro marker used
 * across the app; `signals` marks a Signals feature.
 */
export type ProBadgeIcon = "diamond" | "signals"

export type ProBadgeProps = {
  children: React.ReactNode
  className?: string
  /** Icon rendered in the badge circle. */
  icon?: ProBadgeIcon
  /**
   * Reveal the label in place, as a pill expanding out of the icon, instead of
   * in a tooltip beside it.
   */
  inline?: boolean
  size?: "small" | "large"
}

type ProBadgeVariantProps = Omit<ProBadgeProps, "inline" | "icon"> & {
  icon: ProBadgeIcon
}

// =============================================================================
// Components
// =============================================================================
function TooltipProBadge({
  children,
  className,
  icon,
  size = "small"
}: ProBadgeVariantProps) {
  return (
    <Tooltip standalone>
      <Tooltip.Trigger
        className={cn(circleStyles({ size }), tooltipTriggerStyles, className)}
      >
        <Icon name={icon} size="xs" />
        <VisuallyHidden>{children}</VisuallyHidden>
      </Tooltip.Trigger>
      <Tooltip.Content>{children}</Tooltip.Content>
    </Tooltip>
  )
}

function InlineProBadge({
  children,
  className,
  icon,
  size
}: ProBadgeVariantProps) {
  const [pressedOpen, setPressedOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const labelId = useId()

  // Only a mouse opens the badge on hover: a tap fires `pointerenter` too, and
  // the press that follows it would toggle the badge straight back closed.
  const setHoveredFromMouse =
    (hovering: boolean) => (event: React.PointerEvent) => {
      if (event.pointerType === "mouse") {
        setHovered(hovering)
      }
    }

  return (
    <span
      className={cn(circleStyles({ size }), "relative inline-block", className)}
    >
      <Collapsible.Root
        render={<span />}
        open={pressedOpen || hovered}
        onOpenChange={(_nextOpen, details) => {
          if (details.reason === "trigger-press") {
            setPressedOpen((wasPressedOpen) => !wasPressedOpen)
          }
        }}
        className={collapsibleStyles({ size })}
        onPointerEnter={setHoveredFromMouse(true)}
        onPointerLeave={setHoveredFromMouse(false)}
      >
        <Collapsible.Trigger
          aria-labelledby={labelId}
          className={collapsibleTriggerStyles({ size })}
        >
          <Icon name={icon} size="xs" />
        </Collapsible.Trigger>
        <Collapsible.Panel
          keepMounted
          render={<span />}
          className={panelStyles({ size })}
        >
          <span id={labelId} className={cn(labelStyles)}>
            {children}
          </span>
        </Collapsible.Panel>
      </Collapsible.Root>
    </span>
  )
}

/**
 * A badge marking a pro feature. Renders as an icon that reveals its label on
 * hover, click or keyboard press — in a tooltip by default, or as a pill
 * expanding out of the icon with `inline`.
 */
export function ProBadge({
  inline = false,
  icon = "diamond",
  ...props
}: ProBadgeProps) {
  return inline ? (
    <InlineProBadge icon={icon} {...props} />
  ) : (
    <TooltipProBadge icon={icon} {...props} />
  )
}
